// The heading every page of the Amazon overview opens with, and the states its buildings
// load through.
import type { ReactNode } from "react";

import { useAmazonOwners } from "../../api/hooks/useAmazonOwners";
import { ShareButton } from "../../components/SharePanel";
import { SignOutButton } from "../../components/SignOutButton";
import { formatDate, today } from "./dates";
import { OVERVIEW_PATH } from "./overviewPages";
import type { useShownBuildings } from "./useShownBuildings";
import { ViewAsBanner, ViewAsButton } from "./ViewAs";
import styles from "./amazonOverview.module.css";

/** Each page's heading is its nav label, with View as and Share top right for InRange.
 * ``switcher`` sits right after the title, in its type, outside the h1 so its menu keeps
 * its own type and stays out of the heading's name (as My actions' person switcher). */
export const OverviewHeading = ({
  title,
  switcher,
  aside,
}: {
  title: string;
  switcher?: ReactNode;
  aside?: ReactNode;
}) => {
  const owners = useAmazonOwners();
  const people = owners.data?.people ?? [];
  const assignedTo = (email: string): number => {
    const person = people.find((p) => p.email === email);
    return person
      ? (owners.data?.owners ?? []).filter((o) => o.assignee === person.person_id).length
      : 0;
  };
  return (
    <>
      <ViewAsBanner />
      <div className={styles.headingRow}>
        {switcher ? (
          <div className={styles.headingWith}>
            <h1 className={styles.heading}>{title}</h1>
            {switcher}
          </div>
        ) : (
          <h1 className={styles.heading}>{title}</h1>
        )}
        <span className={styles.asOf}>
          All data reflects current status as of {formatDate(today())}
        </span>
        <span className={styles.headingActions}>
          {aside}
          <ViewAsButton people={people} />
          <ShareButton pageKey="amazon-overview" path={OVERVIEW_PATH} assignedTo={assignedTo} />
          <SignOutButton />
        </span>
      </div>
    </>
  );
};

export const WhenLoaded = ({
  data,
  children,
}: {
  data: Pick<ReturnType<typeof useShownBuildings>, "buildings" | "notes">;
  children: () => ReactNode;
}) => {
  const { buildings, notes } = data;
  if (buildings.isLoading || notes.isLoading)
    return <p className={styles.muted}>Loading the Amazon buildings…</p>;
  if (buildings.error || !buildings.data)
    return <p className={styles.error}>Could not load the Amazon buildings.</p>;
  if (buildings.data.buildings.length === 0)
    return <p className={styles.muted}>No building is coded as Amazon's yet.</p>;
  return (
    <>
      {notes.isError && (
        <p className={styles.error}>
          Could not load the values written over this page, so it shows the platform's own.
          Reload to try again.
        </p>
      )}
      {children()}
    </>
  );
};
